import { TransactionTypesEnum, convertTradeRecordToTradeHistoryRecord } from '@models/riskManagement/TradeHistory';
import { getFilteredTradeHistoryRecorsdApiByTransactionTypes } from '../dealsStore';
import { OrderRequestSourceType } from '@api/fbe/core/OrderRequestSourceType';
import { TradeHistoryRecord } from '@api/fbe/core/TradeHistoryRecord';

export type ReportRecord = {
    TOTAL: number;
    WT_Count: number;
    WT_FT: number;
    WT_Non_FT_Total: number;
    WT_FT_OCT_OFF: number;
    WT_FT_OCT_ON: number;
    WT_OTHER: number;
    WT_Non_FT_OCT_ON: number;
    APP_Count: number;
    App_FT_Total: number;
    App_Non_FT_Total: number;
    App_FT_OCT_OFF: number;
    App_FT_OCT_ON: number;
    App_Non_FT_OCT_OFF: number;
    App_Non_FT_OCT_ON: number;
    UNKNOWN_Count_AND_NULL_Count: number;

    DELIVER_TO_OTHER: string;

    UNKNOWN_Count: number;
    NULL_Count: number;
    ST_Count: number;
    API_Count: number;
    MANAGER_Count: number;
    WT_OCT: number;
    APP_OCT: number;
};

export const defaultReportRecord: ReportRecord = {
    TOTAL: 0,
    WT_Count: 0,
    WT_FT: 0,
    WT_Non_FT_Total: 0,
    WT_FT_OCT_OFF: 0,
    WT_FT_OCT_ON: 0,
    WT_OTHER: 0,
    WT_Non_FT_OCT_ON: 0,
    APP_Count: 0,
    App_FT_Total: 0,
    App_Non_FT_Total: 0,
    App_FT_OCT_OFF: 0,
    App_FT_OCT_ON: 0,
    App_Non_FT_OCT_OFF: 0,
    App_Non_FT_OCT_ON: 0,
    UNKNOWN_Count_AND_NULL_Count: 0,

    DELIVER_TO_OTHER: '------',

    UNKNOWN_Count: 0,
    NULL_Count: 0,
    ST_Count: 0,
    API_Count: 0,
    MANAGER_Count: 0,
    WT_OCT: 0,
    APP_OCT: 0,
};

export const getTradeHistoryReportData = ({
    tradeHistory,
    baseCounters,
    transactionTypes,
}: {
    tradeHistory: TradeHistoryRecord[];
    baseCounters: ReportRecord;
    transactionTypes?: TransactionTypesEnum[];
}): ReportRecord => {
    const filteredRawData = getFilteredTradeHistoryRecorsdApiByTransactionTypes(tradeHistory, transactionTypes);
    const data = filteredRawData.map((record, index) => ({
        key: index,
        ...convertTradeRecordToTradeHistoryRecord(record),
    }));
    return data.reduce(
        (
            acc,
            {
                OrderRequestSourceType: SourceType,
                IsCreatedViaFastTrader: FT,
                IsCreatedViaQuickTrading: OCT,
                GroupName,
            },
        ): ReportRecord => {
            const isApp = OrderRequestSourceType.MOBILE_APP.valueOf() === SourceType;
            const isWT = OrderRequestSourceType.WEB_TRADER.valueOf() === SourceType;
            const isAPI = OrderRequestSourceType.TRADING_API.valueOf() === SourceType;
            const isSocialTrading = OrderRequestSourceType.SOCIAL_TRADING.valueOf() === SourceType;

            if (GroupName && GroupName.toLowerCase().includes('test')) {
                return acc;
            }
            return {
                ...acc,
                TOTAL: acc.TOTAL + 1,
                WT_Count: isWT ? acc.WT_Count + 1 : acc.WT_Count,
                WT_FT: isWT && FT ? acc.WT_FT + 1 : acc.WT_FT,
                WT_Non_FT_Total: isWT && !FT ? acc.WT_Non_FT_Total + 1 : acc.WT_Non_FT_Total,
                WT_FT_OCT_OFF: isWT && FT && !OCT ? acc.WT_FT_OCT_OFF + 1 : acc.WT_FT_OCT_OFF,
                WT_FT_OCT_ON: isWT && FT && OCT ? acc.WT_FT_OCT_ON + 1 : acc.WT_FT_OCT_ON,
                WT_OTHER: isWT && !FT && !OCT ? acc.WT_OTHER + 1 : acc.WT_OTHER,
                WT_Non_FT_OCT_ON: isWT && !FT && OCT ? acc.WT_Non_FT_OCT_ON + 1 : acc.WT_Non_FT_OCT_ON,

                APP_Count: isApp ? acc.APP_Count + 1 : acc.APP_Count,
                App_FT_Total: isApp && FT ? acc.App_FT_Total + 1 : acc.App_FT_Total,
                App_Non_FT_Total: isApp && !FT ? acc.App_Non_FT_Total + 1 : acc.App_Non_FT_Total,
                App_FT_OCT_OFF: isApp && FT && !OCT ? acc.App_FT_OCT_OFF + 1 : acc.App_FT_OCT_OFF,
                App_FT_OCT_ON: isApp && FT && OCT ? acc.App_FT_OCT_ON + 1 : acc.App_FT_OCT_ON,
                App_Non_FT_OCT_OFF: isApp && !FT && !OCT ? acc.App_Non_FT_OCT_OFF + 1 : acc.App_Non_FT_OCT_OFF,
                App_Non_FT_OCT_ON: isApp && !FT && OCT ? acc.App_Non_FT_OCT_ON + 1 : acc.App_Non_FT_OCT_ON,
                UNKNOWN_Count_AND_NULL_Count:
                    (OrderRequestSourceType.UNKNOWN.valueOf() === SourceType
                        ? acc.UNKNOWN_Count + 1
                        : acc.UNKNOWN_Count) + (SourceType === null ? acc.NULL_Count + 1 : acc.NULL_Count),

                DELIVER_TO_OTHER: '------',

                UNKNOWN_Count:
                    OrderRequestSourceType.UNKNOWN.valueOf() === SourceType ? acc.UNKNOWN_Count + 1 : acc.UNKNOWN_Count,
                NULL_Count: SourceType === null ? acc.NULL_Count + 1 : acc.NULL_Count,
                ST_Count: isSocialTrading ? acc.ST_Count + 1 : acc.ST_Count,
                API_Count: isAPI ? acc.API_Count + 1 : acc.API_Count,
                MANAGER_Count:
                    OrderRequestSourceType.MANAGER.valueOf() === SourceType ? acc.MANAGER_Count + 1 : acc.MANAGER_Count,
                WT_OCT: isWT && OCT ? acc.WT_OCT + 1 : acc.WT_OCT,
                APP_OCT: isApp && OCT ? acc.APP_OCT + 1 : acc.APP_OCT,
            };
        },
        baseCounters,
    );
};

export const getRecordToExport = (data: ReportRecord): Record<string, string> =>
    Object.keys(data).reduce((acc, key) => ({ ...acc, [key]: String(data[key]) }), {});
